'use client';

import { useTranslations } from 'next-intl';
import type { FC, KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { ProductLineDuration } from '@/app/components/icons/ProductLineDuration';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import type { OfflineAvatarTimelineAction } from '@/app/types';
import { cn } from '@/app/utils';
import {
  appendTimelineAction,
  createTimelineAction,
  findTimelineActionInsertionRange,
  MAX_TIMELINE_ACTION_TEXT_LENGTH,
  normalizeTimelineAction,
  removeTimelineAction,
  updateTimelineActionTime,
} from '../_utils';

interface OfflineAvatarTimelineEditorProps {
  actions: OfflineAvatarTimelineAction[];
  duration: number;
  onChange: (actions: OfflineAvatarTimelineAction[]) => void;
  disabled?: boolean;
}

const formatTimestamp = (value: number) => String(Math.round(value));

const focusTimeInput = (event: MouseEvent<HTMLLabelElement>) => {
  if (event.target instanceof HTMLInputElement) return;

  event.currentTarget.querySelector<HTMLInputElement>('input')?.focus();
};

interface TimelineTimeInputProps {
  id: string;
  value: number;
  ariaLabel: string;
  disabled: boolean;
  min: number;
  max: number;
  onCommit: (value: number) => void;
}

const TimelineTimeInput: FC<TimelineTimeInputProps> = ({ id, value, ariaLabel, disabled, min, max, onCommit }) => {
  const formattedValue = formatTimestamp(value);
  const [draftValue, setDraftValue] = useState(formattedValue);

  useEffect(() => {
    setDraftValue(formattedValue);
  }, [formattedValue]);

  const handleCommitValue = () => {
    if (draftValue === '') {
      setDraftValue(formattedValue);
      return;
    }

    const numericValue = Number(draftValue);
    if (!Number.isFinite(numericValue)) {
      setDraftValue(formattedValue);
      return;
    }

    onCommit(numericValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <input
      id={id}
      type="number"
      disabled={disabled}
      min={min}
      max={max}
      step={1}
      value={draftValue}
      aria-label={ariaLabel}
      onChange={(event) => {
        const nextValue = event.target.value;
        if (nextValue === '') {
          setDraftValue(nextValue);
          return;
        }

        const numericValue = Number(nextValue);
        if (Number.isFinite(numericValue)) {
          setDraftValue(String(Math.min(Math.max(numericValue, min), max)));
          return;
        }

        setDraftValue(nextValue);
      }}
      onBlur={handleCommitValue}
      onKeyDown={handleKeyDown}
      style={{ width: `${Math.max(draftValue.length, 1)}ch` }}
      className="bg-transparent p-0 text-center text-xs outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  );
};

export const OfflineAvatarTimelineEditor: FC<OfflineAvatarTimelineEditorProps> = ({
  actions,
  duration,
  onChange,
  disabled = false,
}) => {
  const t = useTranslations('tools.offlineAvatar.timeline');
  const maxDuration = Math.max(duration, 1);
  const sortedActions = [...actions].sort((a, b) => a.start - b.start);
  const [defaultActions, setDefaultActions] = useState<OfflineAvatarTimelineAction[]>([]);
  useEffect(() => {
    // 默认动作包含随机 ID，只能在客户端挂载后创建，避免 Next.js 预渲染阶段访问 crypto。
    setDefaultActions([createTimelineAction(0, Math.min(5, maxDuration), maxDuration)]);
  }, [maxDuration]);
  const editableActions = sortedActions.length > 0 ? sortedActions : defaultActions;
  const canAdd = !disabled && Boolean(findTimelineActionInsertionRange(editableActions, maxDuration));

  const handleAdd = () => {
    const nextActions = appendTimelineAction(editableActions, maxDuration);
    if (nextActions === editableActions) return;
    onChange(nextActions);
  };

  const handleUpdate = (id: string, patch: Partial<OfflineAvatarTimelineAction>) => {
    onChange(
      editableActions.map((action) =>
        action.id === id ? normalizeTimelineAction({ ...action, ...patch }, maxDuration) : action,
      ),
    );
  };

  const handleTimeCommit = (id: string, field: 'start' | 'end', value: number) => {
    onChange(updateTimelineActionTime(editableActions, id, field, value, maxDuration));
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-sm text-system-white">
          <span>{t('title')}</span>
          <CustomTooltip content={t('referenceActionTip')} side="top">
            <button
              type="button"
              aria-label={t('referenceActionTip')}
              className="flex size-4 items-center justify-center text-system-white40 transition-colors hover:text-system-white"
            >
              <BaseLineInfo className="size-4" />
            </button>
          </CustomTooltip>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className={cn(
            'flex h-8 shrink-0 items-center gap-1.5 rounded-8 border border-system-white08 bg-[#1A1C20] px-3 text-xs transition-colors disabled:cursor-not-allowed disabled:text-system-white24',
            canAdd ? 'text-system-white hover:bg-[#22252a]' : 'text-system-white24',
          )}
        >
          <BaseLinePlus className="size-4" />
          <span>{t('add')}</span>
        </button>
      </div>

      <div className="space-y-3">
        {editableActions.map((action) => (
          <div
            key={action.id}
            className={cn(
              'relative min-w-0 rounded-12 border border-system-white08 bg-[#1A1C20] p-3 transition-opacity',
              disabled && 'cursor-not-allowed opacity-40',
            )}
          >
            <div className="flex items-center gap-1">
              <label
                htmlFor={`timeline-${action.id}-start`}
                className={cn(
                  'flex h-7 shrink-0 items-center justify-center gap-1 rounded-8 bg-system-white12 px-3 py-1.5 text-system-white',
                  !disabled && 'cursor-text',
                )}
                onClick={focusTimeInput}
              >
                <ProductLineDuration className="size-3.5 shrink-0" />
                <TimelineTimeInput
                  id={`timeline-${action.id}-start`}
                  disabled={disabled}
                  value={action.start}
                  min={0}
                  max={maxDuration}
                  ariaLabel={t('start')}
                  onCommit={(value) => handleTimeCommit(action.id, 'start', value)}
                />
                <span className="text-xs">s</span>
              </label>
              <span className="h-px w-[7px] shrink-0 bg-[#7a808c]" aria-hidden="true" />
              <label
                htmlFor={`timeline-${action.id}-end`}
                className={cn(
                  'flex h-7 shrink-0 items-center justify-center gap-1 rounded-8 bg-system-white12 px-3 py-1.5 text-system-white',
                  !disabled && 'cursor-text',
                )}
                onClick={focusTimeInput}
              >
                <ProductLineDuration className="size-3.5 shrink-0" />
                <TimelineTimeInput
                  id={`timeline-${action.id}-end`}
                  disabled={disabled}
                  value={action.end}
                  min={0}
                  max={maxDuration}
                  ariaLabel={t('end')}
                  onCommit={(value) => handleTimeCommit(action.id, 'end', value)}
                />
                <span className="text-xs">s</span>
              </label>
            </div>

            <textarea
              value={action.text}
              maxLength={MAX_TIMELINE_ACTION_TEXT_LENGTH}
              disabled={disabled}
              onChange={(event) => handleUpdate(action.id, { text: event.target.value })}
              placeholder={t('actionPlaceholder')}
              className="mt-3 h-[60px] max-h-[60px] min-h-[60px] w-full resize-none overflow-y-auto bg-transparent p-0 text-sm text-system-white leading-5 outline-none placeholder:text-system-white40"
            />
            {editableActions.length > 1 ? (
              <button
                type="button"
                aria-label={t('delete')}
                disabled={disabled}
                onClick={() => onChange(removeTimelineAction(editableActions, action.id))}
                className="absolute top-3 right-3 flex size-7 items-center justify-center rounded-8 text-system-white40 transition-colors hover:bg-system-white08 hover:text-system-white disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-system-white40"
              >
                <BaseLineClose className="size-4" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
};
